// SDK singleton instance
let sdkInstance = null;
let sdkLoadPromise = null;

/**
 * Get the base URL for the embedded SDK
 * @returns {string} The base URL for the SDK
 */
export const getSDKBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:9801';
  }
  return 'https://embedded.mindcloud.co';
};

/**
 * Load the embedded SDK from the public API
 * @returns {Promise<void>} Promise that resolves when SDK is loaded
 */
const loadEmbeddedSDK = () => {
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  if (window?.MindCloud) {
    return Promise.resolve();
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const baseUrl = getSDKBaseUrl();
    const isDev = process.env.NODE_ENV === 'development';
    const assetName = isDev ? 'sdk.1.0.0.umd.js' : 'sdk.1.0.0.min.js';
    const cacheBuster = isDev ? `?t=${Date.now()}` : '';
    script.src = `${baseUrl}/assets/embedded/${assetName}${cacheBuster}`;
    script.async = true;

    script.onload = () => {
      if (window?.MindCloud) {
        resolve();
      } else {
        reject(new Error('SDK loaded but MindCloud object not available'));
      }
    };

    script.onerror = () => {
      reject(new Error('Failed to load embedded SDK'));
    };

    document.head.appendChild(script);
  });

  return sdkLoadPromise;
};

/**
 * Get or create the embedded SDK instance
 * @returns {Promise<object>} Promise that resolves to the embedded SDK instance
 */
export const getEmbeddedSDK = async () => {
  await loadEmbeddedSDK();

  if (!window?.MindCloud) {
    throw new Error('Embedded SDK not available!');
  }

  if (!sdkInstance) {
    sdkInstance = window.MindCloud({ isDevelopment: process.env.NODE_ENV === 'development' });
  }

  return sdkInstance;
};

/**
 * Reset the SDK instance (useful for testing or when switching contexts)
 */
export const resetSDK = () => {
  sdkInstance = null;
};

/**
 * Check if SDK is available
 * @returns {boolean} True if SDK is available
 */
export const isSDKAvailable = () => {
  return Boolean(window?.MindCloud);
};

/**
 * Load and initialize the embedded SDK
 * @returns {Promise<object>} Promise that resolves to the embedded SDK instance
 */
export const initializeEmbeddedSDK = async () => {
  await loadEmbeddedSDK();
  return getEmbeddedSDK();
};
